import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Button, Space, Input, List, Row, Col, Tag, Grid, Select, message, Pagination } from 'antd';
import { PlusOutlined, SearchOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { DataTable, AntDColumn } from '@/components/DataTable';
import { Amenity } from '@/types/resources/property';
import { getAmenities, addAmenity, updateAmenity, deleteAmenity, getBlocks } from '@/api/properties';
import AmenityFormModal from '@/components/features/resources/properties/AmenityFormModal';
import { useAuth } from '@/contexts/AuthContext';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';

const { useBreakpoint } = Grid;

// Constants
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const API_STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

export const Amenities: React.FC = () => {
  const { t } = useTranslation(['properties', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const queryClient = useQueryClient();
  const { firestoreUser } = useAuth();
  
  // Check if this is the active tab
  const isActiveTab = location.pathname.includes('/amenities');
  
  // State
  const [searchText, setSearchText] = useState('');
  const [selectedBlock, setSelectedBlock] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedAmenity, setSelectedAmenity] = useState<Amenity | undefined>();
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [lastDocs, setLastDocs] = useState<QueryDocumentSnapshot[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [amenityToDelete, setAmenityToDelete] = useState<Amenity | null>(null);

  // Queries
  const { data: amenitiesData = { amenities: [], lastDoc: undefined, total: 0 }, isLoading } = useQuery({
    queryKey: ['amenities', currentPage, pageSize, searchText, selectedBlock, selectedStatus, firestoreUser?.workspace?.id],
    queryFn: async () => {
      if (!firestoreUser) {
        throw new Error('User not authenticated');
      }
      const lastDoc = currentPage > 1 ? lastDocs[currentPage - 1] : undefined;
      return getAmenities({
        searchTerm: searchText,
        pageSize,
        currentPage,
        lastDoc,
        firestoreUser
      });
    },
    staleTime: API_STALE_TIME_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    placeholderData: (previousData) => previousData,
    enabled: !!firestoreUser && isActiveTab, // Only run query if firestoreUser is available AND this is the active tab
  });

  const { data: blocksData = { blocks: [] } } = useQuery({
    queryKey: ['blocks', 'all', firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getBlocks({
        searchTerm: '',
        pageSize: 1000,
        firestoreUser
      });
    },
    staleTime: API_STALE_TIME_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    enabled: !!firestoreUser && isActiveTab && isModalVisible, // Only run query when modal is open and this tab is active
  });

  // Update lastDocs when new data arrives
  React.useEffect(() => {
    if (amenitiesData.lastDoc) {
      setLastDocs(prev => {
        const newLastDocs = [...prev];
        newLastDocs[currentPage - 1] = amenitiesData.lastDoc!;
        return newLastDocs;
      });
    }
  }, [amenitiesData.lastDoc, currentPage]);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
    setLastDocs([]);
  }, [searchText, selectedBlock, selectedStatus, pageSize]);

  // Mutations
  const addMutation = useMutation({
    mutationFn: (amenity: Omit<Amenity, 'id' | 'activityRecords' | 'workspace'>) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return addAmenity(amenity, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('amenity.messages.addSuccess'));
      queryClient.invalidateQueries({ queryKey: ['amenities'] });
      setIsModalVisible(false);
    },
    onError: (error) => {
      message.error(t('amenity.messages.addError'));
      console.error('Error adding amenity:', error);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Amenity> }) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return updateAmenity(id, data, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('amenity.messages.updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['amenities'] });
      setIsModalVisible(false);
    },
    onError: (error) => {
      message.error(t('amenity.messages.updateError'));
      console.error('Error updating amenity:', error);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteAmenity(id, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('amenity.messages.deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['amenities'] });
    },
    onError: (error) => {
      message.error(t('amenity.messages.deleteError'));
      console.error('Error deleting amenity:', error);
    }
  });

  // Handlers
  const handleCreate = useCallback(() => {
    setFormMode('create');
    setSelectedAmenity(undefined);
    setIsModalVisible(true);
  }, []);

  const handleEdit = useCallback((amenity: Amenity) => {
    setFormMode('edit');
    setSelectedAmenity(amenity);
    setIsModalVisible(true);
  }, []);

  const handleDelete = useCallback((amenity: Amenity) => {
    setAmenityToDelete(amenity);
    setShowDeleteModal(true);
  }, []);

  const handleBulkDelete = useCallback((_: any, selectedRows: Amenity[]) => {
    // TODO: Implement bulk delete functionality
    console.log('Bulk delete amenities:', selectedRows);
  }, []);

  const handleBulkUpload = useCallback(() => {
    // TODO: Implement bulk upload functionality
    console.log('Bulk upload amenities');
  }, []);

  const handleSubmit = useCallback((values: Partial<Amenity>) => {
    if (selectedAmenity?.id) {
      updateMutation.mutate({ id: selectedAmenity.id, data: values });
    } else {
      addMutation.mutate(values as Omit<Amenity, 'id' | 'activityRecords' | 'workspace'>);
    }
  }, [selectedAmenity, addMutation, updateMutation]);

  const handleViewDetails = useCallback((amenity: Amenity) => {
    navigate(`/resources/properties/amenities/${amenity.id}`, { state: { amenity } });
  }, [navigate]);

  const handlePageChange = useCallback((page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
  }, [pageSize]);

  // Memoized values
  const filteredData = useMemo(() => {
    if (!amenitiesData.amenities) return [];
    // For demo mode, we'll do client-side filtering for block and status
    // In a real implementation, these would be passed to the API
    return amenitiesData.amenities.filter(amenity => {
      const matchesBlock = !selectedBlock || amenity.block?.id === selectedBlock;
      const matchesStatus = !selectedStatus || amenity.status === selectedStatus;
      return matchesBlock && matchesStatus;
    });
  }, [amenitiesData.amenities, selectedBlock, selectedStatus]);

  const uniqueBlocks = useMemo(() => 
    blocksData.blocks.map(block => ({ value: block.id, label: block.name })), 
    [blocksData.blocks]
  );

  const columns: AntDColumn<Amenity>[] = [
    {
      title: t('amenity.details.attributes.name'),
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: t('amenity.details.attributes.type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color="blue">{t(`amenity.types.${type}`)}</Tag>
      )
    },
    {
      title: t('amenity.details.attributes.block'),
      dataIndex: ['block', 'name'],
      key: 'block'
    },
    {
      title: t('amenity.details.attributes.description'),
      dataIndex: 'description',
      key: 'description'
    },
    {
      title: t('amenity.details.attributes.capacity'),
      dataIndex: 'capacity',
      key: 'capacity'
    },
    {
      title: t('amenity.details.attributes.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : status === 'maintenance' ? 'orange' : 'red'}>
          {t(`amenity.status.${status}`)}
        </Tag>
      )
    }
  ];

  const renderAmenityCard = (amenity: Amenity) => (
    <Card
      key={amenity.id}
      hoverable
      style={{ marginBottom: 16 }}
      actions={[
        <Button
          key="view"
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(amenity)}
        >
          {t('common:actions.view')}
        </Button>,
        <Button
          key="edit"
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEdit(amenity)}
        >
          {t('common:actions.edit')}
        </Button>,
        <Button
          key="delete"
          type="link"
          icon={<DeleteOutlined />}
          danger
          onClick={() => handleDelete(amenity)}
        >
          {t('common:actions.delete')}
        </Button>,
      ]}
    >
      <Card.Meta
        title={amenity.name}
        description={
          <Space direction="vertical" size="small">
            <div>
              <Tag color="blue">{t(`amenity.types.${amenity.type}`)}</Tag>
            </div>
            {amenity.block && <div>{amenity.block.name}</div>}
            <div>{amenity.description}</div>
            <div>{t('amenity.details.attributes.capacity')}: {amenity.capacity}</div>
            <div>
              <Tag color={amenity.status === 'active' ? 'green' : amenity.status === 'maintenance' ? 'orange' : 'red'}>
                {t(`amenity.status.${amenity.status}`)}
              </Tag>
            </div>
          </Space>
        }
      />
    </Card>
  );

  return (
    <div style={{ width: '100%' }}>
      {isMobile ? (
        <>
          <List
            dataSource={filteredData}
            renderItem={renderAmenityCard}
            rowKey="id"
            grid={{ gutter: 16, xs: 1, sm: 1 }}
            header={
              <>
                <Row className="search-filter-section" gutter={[{ xs: 8, sm: 16 }, { xs: 8, sm: 16 }]} style={{ marginBottom: 16 }} align="middle">
                  <Col xs={24} sm={16} md={18}>
                    <Input
                      className="search-input"
                      id="amenities-search-input"
                      placeholder={t('common:ui.search')}
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      prefix={<SearchOutlined />}
                      allowClear
                    />
                  </Col>
                  <Col xs={24} sm={8} md={6} style={{ textAlign: screens.xs ? 'left' : 'right', marginTop: screens.xs ? 8 : 0 }}>
                    <Button className="add-record-button" id="amenities-add-button" type="primary" onClick={handleCreate} icon={<PlusOutlined />} block={screens.xs}>
                      {t('amenity.actions.add')}
                    </Button>
                  </Col>
                </Row>
                <div style={{ 
                  marginBottom: 16,
                  overflowX: 'auto',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  gap: '8px'
                }}>
                  <Select
                    placeholder={t('amenity.fields.block')}
                    value={selectedBlock}
                    onChange={setSelectedBlock}
                    allowClear
                    style={{ minWidth: 120 }}
                    options={uniqueBlocks}
                  />
                  <Select
                    placeholder={t('amenity.fields.status')}
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                    allowClear
                    style={{ minWidth: 120 }}
                    options={[
                      { value: 'active', label: t('amenity.status.active') },
                      { value: 'maintenance', label: t('amenity.status.maintenance') },
                      { value: 'inactive', label: t('amenity.status.inactive') }
                    ]}
                  />
                </div>
              </>
            }
            loading={isLoading}
            pagination={false}
          />
          <div className="pagination-section" style={{ 
            marginTop: 16, 
            textAlign: 'center',
            padding: '0 8px'
          }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={amenitiesData.total}
              onChange={handlePageChange}
              showSizeChanger={!screens.xs}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              showQuickJumper={!screens.xs}
              showTotal={!screens.xs ? (total: number, range: [number, number]) => 
                t('common:pagination.showing', { 
                  start: range[0], 
                  end: range[1], 
                  total 
                }) : undefined
              }
              size={screens.xs ? 'small' : 'default'}
              simple={screens.xs}
              style={{ 
                display: 'flex', 
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: '8px'
              }}
            />
          </div>
        </>
      ) : (
        <>
          <Card className="data-table-card">
            <DataTable
              className="data-table"
              dataSource={filteredData}
              columns={columns}
              loading={isLoading}
              rowKey="id"
              onEdit={handleEdit}
              onDelete={handleDelete}
              onView={handleViewDetails}
              onBulkDelete={handleBulkDelete}
              onBulkUpload={handleBulkUpload}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: amenitiesData.total,
                onChange: handlePageChange,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
                showTotal: (total: number, range: [number, number]) => 
                  t('common:pagination.showing', { 
                    start: range[0], 
                    end: range[1], 
                    total 
                  })
              }}
              headerActions={
                <Space>
                  <Input
                    className="search-input"
                    placeholder={t('common:searchPlaceholder', { entity: t('amenity.title') })}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    prefix={<SearchOutlined />}
                    style={{ width: 300 }}
                    allowClear
                  />
                  <Select
                    placeholder={t('amenity.fields.block')}
                    value={selectedBlock}
                    onChange={setSelectedBlock}
                    allowClear
                    style={{ width: 150 }}
                    options={uniqueBlocks}
                  />
                  <Select
                    placeholder={t('amenity.fields.status')}
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                    allowClear
                    style={{ width: 150 }}
                    options={[
                      { value: 'active', label: t('amenity.status.active') },
                      { value: 'maintenance', label: t('amenity.status.maintenance') },
                      { value: 'inactive', label: t('amenity.status.inactive') }
                    ]}
                  />
                  <Button className="add-record-button" type="primary" onClick={handleCreate} icon={<PlusOutlined />}>
                    {t('amenity.actions.add')}
                  </Button>
                </Space>
              }
            />
          </Card>
        </>
      )}
      
      <AmenityFormModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSubmit={handleSubmit}
        initialValues={selectedAmenity}
        title={formMode === 'create' ? t('amenity.actions.add') : t('amenity.actions.edit')}
        blocks={blocksData.blocks.map(block => ({ id: block.id, name: block.name }))}
      />

      <DeleteConfirmationModal
        visible={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={() => {
          if (amenityToDelete) {
            deleteMutation.mutate(amenityToDelete.id);
            setShowDeleteModal(false);
          }
        }}
        title={t('properties:amenity.messages.deleteConfirm')}
      />
    </div>
  );
};

export default Amenities; 